import type { ReactElement } from "react";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../../../../shared/components/ui/table";
import type { BulkEditTitle } from "../types";
import { QuickStatsRow } from "./QuickStatsRow";

interface QuickStatsTableProps {
  readonly titles: BulkEditTitle[];
  readonly selectedIds: Set<string>;
  readonly onSelectAll: (selected: boolean) => void;
  readonly onSelectOne: (titleId: string, selected: boolean) => void;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
  readonly onManageCollections: (titleId: string, titleName: string) => void;
}

/**
 * Quick Stats table view for bulk editor
 * Displays analytics metrics for each title
 */
export function QuickStatsTable({
  titles,
  selectedIds,
  onSelectAll,
  onSelectOne,
  onDelete,
  onDuplicate,
  onManageCollections,
}: Readonly<QuickStatsTableProps>): ReactElement {
  const allSelected = titles.length > 0 && titles.every((t) => selectedIds.has(t.id));

  return (
    <div className="border border-neutral-800 rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-neutral-900/50">
            <TableHead className="w-12">
              <Checkbox checked={allSelected} onCheckedChange={onSelectAll} aria-label="Select all" />
            </TableHead>
            <TableHead className="w-[250px] flex pt-[14px]">Title</TableHead>
            <TableHead className="w-[150px] text-center">Views</TableHead>
            <TableHead className="w-[170px] text-center">Watch Time</TableHead>
            <TableHead className="w-[170px] text-center">Completion</TableHead>
            <TableHead className="w-[150px] text-center">Likes</TableHead>
            <TableHead className="w-[150px] text-center">Watchlist</TableHead>
            <TableHead className="w-[170px] text-center">Collections</TableHead>
            <TableHead className="w-[100px] text-left">Status</TableHead>
            <TableHead className="w-[100px] text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {titles.map((title) => (
            <QuickStatsRow
              key={title.id}
              title={title}
              isSelected={selectedIds.has(title.id)}
              onSelect={onSelectOne}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onManageCollections={onManageCollections}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
